// =====================================================================
// src/lib/checkin.js — o que a tela de Check-in decide, sem DOM
//
// Puro. A regra (quem faz check-in onde, documento obrigatório, menor
// acompanhado, check-out por reserva) mora em 0017; aqui se agrupa o
// painel do dia que `checkin_do_dia` devolve (uma linha por hóspede) em
// reservas com a sua gente, e se decide que botão cada linha mostra.
// =====================================================================
import { dataISO, somarDias, noites, dataCurta } from './reservas.js';

const semAcento = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const chaveQuarto = n => { const m = /^(\d+)(.*)$/.exec(String(n || '')); return m ? m[1].padStart(6, '0') + m[2].toLowerCase() : 'z' + String(n || '').toLowerCase(); };
const digitos = s => String(s || '').replace(/\D/g, '');

export const FASES_CHECKIN = Object.freeze({
  chega: { rotulo: 'Chega hoje', classe: 'badge-blue' },
  hospedada: { rotulo: 'Hospedada', classe: 'badge-green' },
  sai: { rotulo: 'Sai hoje', classe: 'badge-yellow' },
  // 0087: passou da data e o hóspede continua no quarto (o check-out não foi feito)
  atrasada: { rotulo: 'Saída atrasada', classe: 'badge-orange' },
  concluida: { rotulo: 'Saiu', classe: 'badge-gray' },
});

// A fase de uma reserva vista do dia do painel.
export function faseCheckin(r, dia) {
  if (r.situacao === 'concluida') return 'concluida';
  if (r.check_out < dia) return 'atrasada';   // 0087: devia ter saído e continua dentro
  if (r.check_in === dia) return 'chega';
  if (r.check_out === dia) return 'sai';
  return 'hospedada';
}

// "14:32" no fuso de quem olha.
export function horaCurta(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

// Fila de cada pessoa: chegou · saiu · pronto (documento ok — fila rápida)
// · pendente (falta documento — fila lenta) · menor (entra com o titular).
export function filaDoHospede(h, gente) {
  if (h.saida_em) return 'saiu';
  if (h.chegada_em) return 'chegou';
  if (h.documento_ok) return 'pronto';
  if (h.menor && (gente || []).some(t => t.principal && t.documento_ok)) return 'menor';
  return 'pendente';
}

// O painel que a função devolve (uma linha por hóspede) vira reservas com
// a sua gente, ordenadas por fase e quarto.
export function agruparCheckin(linhas, dia) {
  const por = new Map();
  for (const l of linhas || []) {
    if (!por.has(l.reserva_id)) {
      por.set(l.reserva_id, { id: l.reserva_id, quarto: l.quarto, andar: l.andar, capacidade: l.capacidade, check_in: l.check_in, check_out: l.check_out,
        situacao: l.situacao, numero: l.numero, plataforma: l.plataforma, imobiliaria: l.imobiliaria,
        chegada_prevista: l.chegada_prevista || null, placa_veiculo: l.placa_veiculo || null,
        atendimento_iniciado_em: l.atendimento_iniciado_em || null, gente: [] });
    }
    if (l.hospede_id) por.get(l.reserva_id).gente.push({ id: l.hospede_id, nome: l.nome, principal: !!l.principal, documento_ok: !!l.documento_ok, documento: l.documento || null,
      tipo_documento: l.tipo_documento || null, menor: !!l.menor, chegada_em: l.chegada_em || null, saida_em: l.saida_em || null });
  }
  const ordemFase = ['chega', 'sai', 'hospedada', 'concluida'];
  return [...por.values()].map(r => {
    r.gente.sort((a, b) => (b.principal - a.principal) || String(a.nome).localeCompare(String(b.nome)));
    for (const h of r.gente) h.fila = filaDoHospede(h, r.gente);
    const chegaram = r.gente.filter(h => h.chegada_em).length;
    const titular = r.gente.find(h => h.principal) || null;
    return { ...r, fase: faseCheckin(r, dia), chegaram, total: r.gente.length, semGente: r.gente.length === 0, titular,
      aguardaAtendimento: aguardaAtendimento(r, chegaram, dia),
      titularChegou: !!(titular && titular.chegada_em), pendentes: r.gente.filter(h => h.fila === 'pendente').length,
      lotacao: `${chegaram}/${r.gente.length}` };
  }).sort((a, b) => (ordemFase.indexOf(a.fase) - ordemFase.indexOf(b.fase)) || chaveQuarto(a.quarto).localeCompare(chaveQuarto(b.quarto)));
}

export const ABAS_CHECKIN = Object.freeze([
  ['chegadas', 'Chegadas', ['chega']],
  ['hospedados', 'Hospedados', ['hospedada']],
  ['saidas', 'Saídas', ['atrasada', 'sai', 'concluida']],
  ['todas', 'Todas', ['chega', 'sai', 'atrasada', 'hospedada', 'concluida']],
]);

export function filtrarCheckin(lista, { aba, texto }, dia) {
  const def = ABAS_CHECKIN.find(a => a[0] === aba) || ABAS_CHECKIN[0];
  const t = semAcento(texto).trim();
  return (lista || []).filter(r => t
    ? [r.quarto, r.numero, r.imobiliaria, ...r.gente.map(h => h.nome)].some(c => semAcento(c).includes(t)) || r.gente.some(h => digitos(h.documento).includes(digitos(t) || '§'))
    : def[2].includes(r.fase));
}

// Os contadores do cabeçalho: reservas que chegam (e quantas já com o
// titular dentro), hospedadas, que saem (e quantas já saíram).
export function resumoCheckin(lista) {
  const l = lista || [];
  const chega = l.filter(r => r.fase === 'chega'), sai = l.filter(r => r.fase === 'sai'), saiu = l.filter(r => r.fase === 'concluida');
  return { chegam: chega.length, chegaram: chega.filter(r => r.titularChegou).length, hospedadas: l.filter(r => r.fase === 'hospedada').length,
    saem: sai.length + saiu.length, sairam: saiu.length, pendentes: l.reduce((n, r) => n + r.pendentes, 0) };
}

// O que uma pessoa pode receber: check-in, desfazer, ou nada (com o motivo
// para a tela explicar em vez de esconder).
// ── ATENDER ANTES DO CHECK-IN (0061, Hugo 25/09/2026, opção A) ─────────
// A reserva que chega e ainda não foi atendida mostra o botão Atender, e
// NÃO os Check-in de cada hóspede: é o Atender que grava o início do
// atendimento no balcão (0060), e sem ele o check-in ficaria fora do
// indicador. Vale para a chegada atrasada também (check-in ontem, ninguém
// dentro). Depois que alguém entrou, os demais da reserva entram direto.
export function aguardaAtendimento(r, chegaram, dia) {
  return r.situacao === 'confirmada' && chegaram === 0 && !r.atendimento_iniciado_em
    && r.check_in <= dia && r.check_out >= dia;
}

export function acaoDoHospede(h, r, { podeEditar }) {
  if (!podeEditar || r.situacao !== 'confirmada') return { acao: null };
  if (h.saida_em) return { acao: null, motivo: 'saiu' };
  if (h.chegada_em) return { acao: 'desfazer' };
  if (r.aguardaAtendimento) return { acao: null };   // o botão é o Atender, no cartão
  if (h.fila === 'pendente') return { acao: null, motivo: 'Sem documento — cadastre em Hóspedes' };
  return { acao: 'checkin' };
}

// Check-out só de reserva confirmada com alguém dentro.
export function podeCheckout(r, { podeEditar }) {
  return !!podeEditar && r.situacao === 'confirmada' && r.gente.some(h => h.chegada_em && !h.saida_em);
}

// 0093 (decisão 1A do Hugo): o check-out se desfaz no MESMO dia do hotel —
// a mesma régua de `desfazer_checkout`, que recusa o resto. O instante do
// check-out é o carimbo de saída que ele pôs em todos (o maior deles);
// `diaDe` diz o dia do HOTEL desse instante (hojeDe, nunca .slice(0, 10)).
export function podeDesfazerCheckout(r, { podeEditar, hoje, diaDe }) {
  if (!podeEditar || !r || r.situacao !== 'concluida') return false;
  const saidas = (r.gente || []).map(h => h.saida_em).filter(Boolean).sort();
  if (!saidas.length) return false;
  return diaDe(saidas[saidas.length - 1]) === hoje;
}

export { dataISO, somarDias, noites, dataCurta };
